import React, { FC, useContext, useEffect, useState } from 'react'
import { initialValues } from './initialValues'
import { Giftcard, OrderFormValues } from './types'

type State = {
  order: OrderFormValues | null
  giftcard: Giftcard | null
  paymentMethod: string
  paymentAction: string
  country: string | null
}
type Methods = {
  setOrder: (order: OrderFormValues) => void
  setGiftcard: (giftcard: Giftcard) => void
  removeGiftcard: () => void
  setPaymentMethod: (paymentMethod) => void
  setPaymentAction: (paymentAction) => void
  clear: () => void
  setCountry: (country: string) => void
}

const initialState: State = {
  order: initialValues,
  giftcard: null,
  paymentMethod: null,
  paymentAction: null,
  country: null
}

const CheckoutContext = React.createContext<State & Methods>({
  ...initialState,
  setOrder: () => {},
  setGiftcard: () => {},
  removeGiftcard: () => {},
  setPaymentMethod: () => {},
  setPaymentAction: () => {},
  clear: () => {},
  setCountry: () => {}
})

type Props = {
  initalOrderData?: OrderFormValues
}

const CheckoutProvider: FC<Props> = ({ children, initalOrderData = null }) => {
  const [state, setState] = useState<State>(initialState)
  const setOrder = (order: OrderFormValues) => {
    window.sessionStorage.setItem(
      'order',
      JSON.stringify({ ...order, acceptTermsAndConditions: false })
    )
    setState(state => ({ ...state, order }))
  }
  const setGiftcard = (giftcard: Giftcard) => setState(state => ({ ...state, giftcard }))
  const removeGiftcard = () => setState(state => ({ ...state, giftcard: null }))
  const setPaymentMethod = paymentMethod => setState(state => ({ ...state, paymentMethod }))
  const setPaymentAction = paymentAction => {
    setState({ ...state, paymentAction })
  }
  const clear = () => setState(initialState)
  const setCountry = country => setState({ ...state, country })

  useEffect(() => {
    const order = window.sessionStorage.getItem('order')

    if (order) {
      setState(state => ({ ...state, order: JSON.parse(order) }))
    }
  }, [])
  return (
    <CheckoutContext.Provider
      value={{
        ...state,
        setOrder,
        setGiftcard,
        removeGiftcard,
        setPaymentMethod,
        setPaymentAction,
        clear,
        setCountry
      }}
    >
      <>{children}</>
    </CheckoutContext.Provider>
  )
}

export function useCheckout() {
  const ctx = useContext(CheckoutContext)
  if (typeof ctx === 'undefined') {
    throw new Error('Can only use checkout context within a CheckoutContext Provider')
  }
  return ctx
}

export default CheckoutProvider
